import {
  Button,
  ButtonGroup,
  H2,
  Modal,
} from "@datapay-by-datacom/design-system";
import { useState } from "react";
import { OrgAccessRequestRole } from "../../../generated/core-api";
import { useOrgCtx } from "../../../org/hooks";
import { OrgUserSummary } from "../../types";
import s from "./OrgAccessModal.module.css";

interface OrgAccessModalProps {
  user: OrgUserSummary;
  isOpen: boolean;
  onClose: () => void;
  onGrant: (orgId: string, orgCategory: string, role: OrgAccessRequestRole) => void;
  isPending: boolean;
}

export function OrgAccessModal({
  user,
  isOpen,
  onClose,
  onGrant,
  isPending,
}: OrgAccessModalProps) {
  const { orgs } = useOrgCtx();
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedRole, setSelectedRole] = useState<OrgAccessRequestRole>("member");

  const existingOrgIds = new Set(user.org_roles.map((r) => r.org_id));
  const availableOrgs = orgs.filter((org) => !existingOrgIds.has(org.id));

  const selectedOrg = availableOrgs.find((org) => org.id === selectedOrgId);

  const handleGrant = () => {
    if (!selectedOrg) return;
    onGrant(selectedOrg.id, selectedOrg.category, selectedRole);
    setSelectedOrgId("");
    setSelectedRole("member");
  };

  const handleClose = () => {
    setSelectedOrgId("");
    setSelectedRole("member");
    onClose();
  };

  return (
    <Modal isOpen={isOpen}>
      <div className={s.modal}>
        <H2>Grant Org Access</H2>
        <p className={s.description}>
          Grant <strong>{user.name || user.email || user.user_id}</strong> access
          to an organization.
        </p>

        <div className={s.field}>
          <label className={s.label} htmlFor="org-select">
            Organization
          </label>
          <select
            id="org-select"
            className={s.select}
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            disabled={isPending}
          >
            <option value="">Select an organization...</option>
            {availableOrgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.category})
              </option>
            ))}
          </select>
          {availableOrgs.length === 0 && (
            <p className={s.hint}>
              This user already has access to all your organizations.
            </p>
          )}
        </div>

        <div className={s.field}>
          <label className={s.label} htmlFor="role-select">
            Role
          </label>
          <select
            id="role-select"
            className={s.select}
            value={selectedRole}
            onChange={(e) =>
              setSelectedRole(e.target.value as OrgAccessRequestRole)
            }
            disabled={isPending}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <ButtonGroup>
          <Button variant="tertiary" onPress={handleClose} isDisabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleGrant}
            isDisabled={!selectedOrgId || isPending}
          >
            {isPending ? "Granting..." : "Grant Access"}
          </Button>
        </ButtonGroup>
      </div>
    </Modal>
  );
}
